/**
 * "It was written correctly and renders nowhere" — the calendar's version of
 * phase 1's *created behind a collapsed folder*, which the OWNER found by hand.
 *
 * `CalendarView` shows `expandedEvents.filter(e => !hiddenCalendars[e.calendar]
 * && matchesCalendarFilter(e, calendarFilter))`, windowed to the current view.
 * So a correctly written appointment can be invisible for THREE independent
 * reasons, and they need three different answers:
 *
 * 1. **Outside the current view window.** An appointment three months out
 *    simply is not in the grid. That is correct, and navigating to it is
 *    forbidden — an MCP caller is an external process and the user may not be
 *    looking at Apiboo at all. **REPORTED.**
 *
 * 2. **`calendarFilter`** (`'all' | 'personal' | 'team'`) is a mode the user
 *    chose from the toolbar, like a search filter. **REPORTED, never touched.**
 *
 * 3. **`hiddenCalendars[key]`** — persisted per-calendar visibility. Phase 1
 *    replaced the per-calendar sidebar with the toolbar filter, so an entry can
 *    only survive from older persisted state, and **there is no UI to clear it
 *    today**. This one is the true analogue of a collapsed folder: a container
 *    the user cannot currently reopen. So it is CLEARED — one key, the one the
 *    write went into, and nothing else in the store is touched.
 *
 * **Never `setFocusedDate`, never `setView`.** Those are the only movable
 * things in `calendarUiStore` and moving either is a navigation — the thing
 * `uiRefresh.js`'s header forbids. There is no selected-appointment state in
 * the calendar at all (the popover, the editor draft, the delete target and the
 * scope dialog are component-local `useState` in `CalendarView`), so there is
 * no "reveal" primitive here and inventing one would mean yanking the view of
 * someone planning next month.
 */
import { useCalendarUiStore } from '../extensions/bundled/calendar/calendarUiStore';
import { eventRange, addDays, startOfDay } from '../utils/calendarDates';

/** The app's own toolbar predicate, in one line, over the record's `calendar`. */
function matchesFilter(calendarKey, filter) {
  if (filter === 'personal') return calendarKey === 'personal';
  if (filter === 'team') return String(calendarKey || '').startsWith('team:');
  return true;
}

/**
 * The view window `CalendarView` derives from `view` + `focusedDate`, in the
 * widest form each view can show. Deliberately GENEROUS: this is a report, and
 * claiming "you will not see it" when the user would is worse than the reverse.
 */
function viewWindow(view, focusedDate) {
  const day = startOfDay(focusedDate);
  if (view === 'month') return [addDays(day, -45), addDays(day, 45)];
  if (view === 'week') return [addDays(day, -8), addDays(day, 9)];
  if (view === 'agenda') return [addDays(day, -1), addDays(day, 31)];
  return [addDays(day, -1), addDays(day, 2)];
}

/**
 * Un-hide the calendar a write landed in, if it was hidden, and report whether
 * the user would see the appointment right now.
 *
 * Returns `{ visibleNow, notVisibleBecause }` — merged into every write result.
 * Best-effort: a store that throws must never turn a landed write into a
 * failure, so this degrades to "we could not tell" rather than erroring.
 */
export function revealCalendarWrite(event) {
  const key = event?.calendar || 'personal';
  try {
    const state = useCalendarUiStore.getState();

    // (3) The one place a store write is spent. ONE key — clearing the whole
    // map would silently un-hide calendars the user hid for their own reasons.
    if (state.hiddenCalendars?.[key]) {
      const next = { ...state.hiddenCalendars };
      delete next[key];
      useCalendarUiStore.setState({ hiddenCalendars: next });
    }

    // (2) A mode the user selected. Reported, not changed.
    if (!matchesFilter(key, state.calendarFilter)) {
      return { visibleNow: false, notVisibleBecause: 'calendar_filter' };
    }

    // (1) Outside the window they are looking at. Reported, never navigated to.
    const [from, to] = viewWindow(state.view, new Date(state.focusedDateMs));
    const { start, end } = eventRange(event);
    if (!(start < to && end > from)) {
      return { visibleNow: false, notVisibleBecause: 'outside_view_window' };
    }
    return { visibleNow: true, notVisibleBecause: null };
  } catch {
    return { visibleNow: true, notVisibleBecause: null };
  }
}
